/**
 * Property Relationship Types
 * 
 * Type definitions for property relationships and graph visualization.
 * Forward-compatible with upcoming backend enhancements.
 */

export enum RelationshipType {
  CALCULATION = 'calculation',
  RATIO = 'ratio',
  AGGREGATION = 'aggregation',
  CONDITION = 'condition',
  SWITCH = 'switch',
  INPUT = 'input',
  DEFAULT = 'default',
  GLOBAL_REF = 'global_ref',
  REFERENCED_ARTIFACT = 'referenced_artifact',
  UNKNOWN = 'unknown'
}

export interface PropertyRelationship {
  id: string;
  sourcePropertyId: string;
  sourcePropertyKey: string;
  targetPropertyId?: string;
  targetPropertyKey?: string;
  relationshipType: RelationshipType;
  calculationType?: string; // e.g., 'add', 'subtract', 'divide', 'aggregation', 'conditional'
  formula?: string; // Human-readable formula like "Earnings = Revenue - Expense"
  metadata?: Record<string, any>; // Additional relationship-specific data
  artifactId?: string; // For cross-artifact relationships
  isGlobalVariable?: boolean; // True if target is a global variable
  globalVariableKey?: string; // If referencing a global variable
}

export interface PropertyGraphNode {
  id: string;
  propertyId: string;
  propertyKey: string;
  propertyType: string;
  relationshipType: RelationshipType;
  description?: string;
  inputSource?: 'user_input' | 'system_generated' | 'referenced_artifact' | 'transformed_reference';
  isRequired?: boolean;
  defaultValue?: string;
  // Forward-compatible fields (optional for current backend)
  relationshipMetadata?: Record<string, any>;
  dependenciesCount?: number;
  dependentsCount?: number;
}

export interface PropertyGraphEdge {
  id: string;
  source: string; // Property ID
  target: string; // Property ID or global variable key
  relationshipType: RelationshipType;
  calculationType?: string;
  formula?: string;
  label?: string; // Display label for the edge
  metadata?: Record<string, any>;
  isGlobalVariable?: boolean; // True if target is a global variable
}

export interface PropertyGraph {
  nodes: PropertyGraphNode[];
  edges: PropertyGraphEdge[];
  relationshipSummary?: {
    calculations: number;
    ratios: number;
    aggregations: number;
    conditions: number;
    switches: number;
    globalRefs: number;
    inputs: number;
    defaults: number;
  };
}

export interface PropertyDependency {
  propertyId: string;
  propertyKey: string;
  relationshipType: RelationshipType;
  calculationType?: string;
  formula?: string;
  depth?: number; // Depth in dependency chain
  isCircular?: boolean; // True if part of circular dependency
}

export interface PropertyDependent {
  propertyId: string;
  propertyKey: string;
  relationshipType: RelationshipType;
  calculationType?: string;
  formula?: string;
  howUsed?: string; // Description of how this property uses the source
}

export interface PropertyRelationships {
  propertyId: string;
  propertyKey: string;
  dependencies: PropertyDependency[];
  dependents: PropertyDependent[];
  relationshipType: RelationshipType;
}

// Forward-compatible interface for future backend graph endpoint
export interface PropertyGraphResponse {
  nodes: PropertyGraphNode[];
  edges: PropertyGraphEdge[];
  relationship_summary?: {
    calculations: number;
    ratios: number;
    aggregations: number;
    conditions: number;
    switches: number;
    global_refs: number;
    inputs: number;
    defaults: number;
  };
}

